import type { LocationModel } from '../../types/location';
import type { PropertyAdEditorFormValues } from '../../types/propertyAdEditor';

import { getDepthLabel } from '../LocationAutocomplete/locationFormatting';

export type PropertyAdLocationValues = Pick<
  PropertyAdEditorFormValues,
  | 'addressEn'
  | 'nameEn'
  | 'title'
  | 'placeId'
  | 'coordinatesLat'
  | 'coordinatesLong'
  | 'locationId'
  | 'districtId'
  | 'buildingName'
>;

export const PROPERTY_AD_LOCATION_FIELD_NAMES = [
  'addressEn',
  'nameEn',
  'title',
  'placeId',
  'coordinatesLat',
  'coordinatesLong',
  'locationId',
  'districtId',
  'buildingName',
] as const satisfies ReadonlyArray<keyof PropertyAdLocationValues>;

export const EMPTY_PROPERTY_AD_LOCATION: PropertyAdLocationValues = {
  addressEn: null,
  nameEn: null,
  title: '',
  placeId: null,
  coordinatesLat: null,
  coordinatesLong: null,
  locationId: null,
  districtId: null,
  buildingName: null,
};

export const getPropertyAdLocationAddress = (location: LocationModel): string => {
  const locationName = location[location.kind] ?? '';
  const depthLabel = getDepthLabel(location);

  return depthLabel ? `${depthLabel}, ${locationName}` : locationName;
};

export const normalizeExistingPropertyAdLocation = (
  location: LocationModel
): PropertyAdLocationValues => {
  const locationName = location[location.kind] ?? null;

  return {
    ...EMPTY_PROPERTY_AD_LOCATION,
    addressEn: getPropertyAdLocationAddress(location),
    nameEn: locationName,
    title: locationName ?? '',
    coordinatesLat: String(location.latitude),
    coordinatesLong: String(location.longitude),
    locationId: location.id,
  };
};

export const normalizeNewPropertyAdLocation = (
  location: LocationModel,
  buildingName: string | null
): PropertyAdLocationValues => {
  const normalizedBuildingName = buildingName?.trim() || null;

  return {
    ...EMPTY_PROPERTY_AD_LOCATION,
    addressEn: getPropertyAdLocationAddress(location),
    nameEn: normalizedBuildingName,
    title: normalizedBuildingName ?? '',
    districtId: location.id,
    buildingName: normalizedBuildingName,
  };
};

export const normalizeNewPropertyAdBuildingName = (
  currentValues: PropertyAdLocationValues,
  buildingName: string
): PropertyAdLocationValues => {
  const normalizedBuildingName = buildingName.trim() || null;

  return {
    ...currentValues,
    nameEn: normalizedBuildingName,
    title: normalizedBuildingName ?? '',
    placeId: null,
    coordinatesLat: null,
    coordinatesLong: null,
    locationId: null,
    buildingName: normalizedBuildingName,
  };
};
